/**
 * B2B Quote Cart — Dawn Theme
 * Intercepts all add-to-cart actions and builds a quote request instead.
 * Submits via Shopify's native /contact endpoint.
 */

const QuoteCart = (() => {
  const STORAGE_KEY = 'b2b_quote_cart';
  let items = [];

  /* ─── Independent audit log (Option C) ─────────────────────────
     Every submitted order is POSTed to a Google Apps Script web app
     that appends it to a Google Sheet the admin can open at any time.
     This is a safety net that is INDEPENDENT of email deliverability:
     even if the Shopify contact-form email is silently dropped by the
     recipient's mail server, the order still lands in the sheet.

     SETUP (one-time, done with the client):
       1. Create a Google Sheet.
       2. Extensions → Apps Script, paste the Code.gs from the setup
          notes, Deploy → New deployment → Web app → Execute as "Me",
          Access "Anyone".
       3. Copy the resulting /exec URL and paste it below.

     Leave as '' to disable logging (the rest of the cart still works).
     The POST is fire-and-forget with keepalive, so it NEVER blocks or
     delays the real form.submit(). It targets script.google.com, NOT
     Shopify, so Shopify's bot detection does not apply. */
  const AUDIT_LOG_URL = ''; // ← paste the Apps Script /exec URL here

  /* ─── Persistence ──────────────────────────────────────────── */

  function load() {
    try { items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { items = []; }
  }

  function save() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }

  // Self-heal: re-fetch metadata for items stuck with "Product" as title
  // or missing image/SKU. Runs once on load.
  function healStoredItems() {
    const broken = items.filter(i =>
      !i.productTitle ||
      i.productTitle === 'Product' ||
      !i.variantTitle ||
      !i.imageUrl ||
      !i.sku
    );
    if (broken.length === 0) return;
    broken.forEach(item => {
      fetch(`/variants/${item.variantId}.json`)
        .then(r => r.json())
        .then(data => {
          const v = data.product_variant || data.variant || {};
          const target = items.find(i => i.variantId === item.variantId);
          if (!target) return;
          if (!target.variantTitle && v.title && v.title !== 'Default Title') {
            target.variantTitle = v.title;
          }
          if (!target.sku && v.sku) target.sku = v.sku;
          const productId = v.product_id;
          if (!productId) { save(); render(); return; }
          return fetchProduct(productId).then(product => {
            if (!product) { save(); render(); return; }
            if ((!target.productTitle || target.productTitle === 'Product') && product.title) {
              target.productTitle = product.title;
            }
            if (!target.imageUrl) {
              const matchedVariant = (product.variants || [])
                .find(pv => String(pv.id) === String(item.variantId));
              const rawImage = matchedVariant?.featured_image?.src
                || product.images?.[0]?.src
                || product.featured_image
                || '';
              if (rawImage) {
                target.imageUrl = rawImage.replace(/(\.[^.?]+)(\?.*)?$/, '_80x80$1$2');
              }
            }
            save();
            render();
          });
        })
        .catch(() => {});
    });
  }

  /* ─── Data manipulation ─────────────────────────────────────── */

  function add(variantId, quantity, productTitle, variantTitle, sku, productHandle, imageUrl) {
    variantId = String(variantId);
    quantity = parseInt(quantity, 10) || 1;

    const idx = items.findIndex(i => i.variantId === variantId);
    if (idx > -1) {
      items[idx].quantity += quantity;
      // Update image/sku if we now have better data
      if (imageUrl && !items[idx].imageUrl) items[idx].imageUrl = imageUrl;
      if (sku && !items[idx].sku) items[idx].sku = sku;
    } else {
      items.push({
        variantId,
        quantity,
        productTitle: productTitle || 'Product',
        variantTitle: variantTitle && variantTitle !== 'Default Title' ? variantTitle : '',
        sku: sku || '',
        productHandle: productHandle || '',
        imageUrl: imageUrl || ''
      });
    }
    save();
    render();
    // Do NOT auto-open drawer — user opens manually via icon
    flashBadge();
  }

  function remove(variantId) {
    items = items.filter(i => i.variantId !== String(variantId));
    save();
    render();
  }

  function setQty(variantId, qty) {
    variantId = String(variantId);
    const item = items.find(i => i.variantId === variantId);
    if (!item) return;
    qty = parseInt(qty, 10);
    if (qty <= 0) { remove(variantId); }
    else { item.quantity = qty; save(); render(); }
  }

  function clear() {
    items = [];
    save();
    render();
  }

  function totalCount() {
    return items.reduce((s, i) => s + i.quantity, 0);
  }

  /* ─── UI ────────────────────────────────────────────────────── */

  function render() {
    updateBadges();
    renderItems();
    syncInputsFromCart();
  }

  /* ─── Hydrate quantity inputs from the quote cart ──────────────
     The quote cart (localStorage) is the single source of truth.
     Shopify's native cart is never written to, so the Liquid-rendered
     `value` on every quantity input is always seeded from an empty
     native cart (0) and is therefore meaningless to us. This function
     forces every on-page quantity input to display the quote-cart
     quantity for its variant (0 if the variant isn't in the quote).

     Runs on every render() — so drawer edits, removals, and clears all
     propagate back to the popup and PDP steppers — and again whenever a
     popup opens (via fixPopupLayout).

     We tag the dispatched change event with __qcInternal so
     interceptBulkQuickAdd ignores it and we don't loop back into
     ourselves. We deliberately set ONLY input.value (not the
     data-cart-quantity attribute) because Dawn's quantity-popover
     attributeChangedCallback re-renders the whole row and wipes our
     injected popup footer. */
  function syncInputsFromCart() {
    const qtyByVariant = new Map(items.map(i => [String(i.variantId), i.quantity]));

    document.querySelectorAll('.quantity__input[data-quantity-variant-id]').forEach(input => {
      const vid = String(input.dataset.quantityVariantId);
      const desired = qtyByVariant.get(vid) || 0;
      if (parseInt(input.value || '0', 10) === desired) return; // already correct
      input.value = desired;
      // Notify Dawn's own listeners (so steppers/labels stay consistent)
      // but tag it so our interceptor doesn't treat it as a user edit.
      const evt = new Event('change', { bubbles: true });
      evt.__qcInternal = true;
      input.dispatchEvent(evt);
    });
  }

  function updateBadges() {
    const count = totalCount();
    document.querySelectorAll('.quote-cart-badge').forEach(el => {
      el.textContent = count;
      el.classList.toggle('quote-cart-badge--hidden', count === 0);
    });
  }

  function renderItems() {
    const container = document.getElementById('quote-drawer-items');
    if (!container) return;

    if (items.length === 0) {
      container.innerHTML = `
        <div class="quote-empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
            <rect x="9" y="3" width="6" height="4" rx="1"/>
            <line x1="9" y1="12" x2="15" y2="12"/>
            <line x1="9" y1="16" x2="13" y2="16"/>
          </svg>
          <p>Your order list is empty.</p>
          <span>Browse products and add them here.</span>
        </div>`;
      return;
    }

    container.innerHTML = items.map(item => `
      <div class="quote-item" data-variant-id="${item.variantId}">
        ${item.imageUrl
          ? `<img class="quote-item-img" src="${escHtml(item.imageUrl)}" alt="${escHtml(item.productTitle)}" loading="lazy">`
          : `<div class="quote-item-img-placeholder"></div>`
        }
        <div class="quote-item-details">
          <p class="quote-item-title">${escHtml(item.productTitle)}</p>
          ${item.variantTitle ? `<p class="quote-item-variant">${escHtml(item.variantTitle)}</p>` : ''}
          ${item.sku ? `<p class="quote-item-sku">SKU: ${escHtml(item.sku)}</p>` : ''}
        </div>
        <div class="quote-item-controls">
          <div class="quote-qty-control">
            <button class="quote-qty-btn" onclick="QuoteCart.setQty('${item.variantId}', ${item.quantity - 1})" aria-label="Decrease">−</button>
            <input class="quote-qty-input" type="number" min="1" value="${item.quantity}"
              onchange="QuoteCart.setQty('${item.variantId}', this.value)" aria-label="Quantity">
            <button class="quote-qty-btn" onclick="QuoteCart.setQty('${item.variantId}', ${item.quantity + 1})" aria-label="Increase">+</button>
          </div>
          <button class="quote-remove-btn" onclick="QuoteCart.remove('${item.variantId}')" aria-label="Remove">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6M14 11v6"/>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </button>
        </div>
      </div>
    `).join('');
  }

  function flashBadge() {
    document.querySelectorAll('.quote-cart-badge').forEach(el => {
      el.classList.remove('quote-cart-badge--pulse');
      void el.offsetWidth;
      el.classList.add('quote-cart-badge--pulse');
    });
  }

  function showDrawer() {
    const drawer = document.getElementById('quote-drawer');
    const overlay = document.getElementById('quote-overlay');
    if (drawer) drawer.classList.add('quote-drawer--open');
    if (overlay) overlay.classList.add('quote-overlay--visible');
    document.body.classList.add('quote-drawer-active');
  }

  function hideDrawer() {
    const drawer = document.getElementById('quote-drawer');
    const overlay = document.getElementById('quote-overlay');
    if (drawer) drawer.classList.remove('quote-drawer--open');
    if (overlay) overlay.classList.remove('quote-overlay--visible');
    document.body.classList.remove('quote-drawer-active');
  }

  // Expand/restore the drawer to/from a full-screen overlay. Pure
  // class toggle — no state to persist. The CSS class
  // .quote-drawer--fullscreen overrides the drawer width/layout; the
  // body class lets us widen the item grid only while expanded.
  function toggleFullscreen() {
    const drawer = document.getElementById('quote-drawer');
    if (!drawer) return;
    const isFull = drawer.classList.toggle('quote-drawer--fullscreen');
    document.body.classList.toggle('quote-fullscreen-active', isFull);
    const btn = drawer.querySelector('.quote-drawer-fullscreen');
    if (btn) btn.setAttribute('aria-pressed', String(isFull));
  }

  /* ─── Form submission ───────────────────────────────────────── */

  function buildBody(formValues) {
    const lines = [
      '=== B2B QUOTE REQUEST ===',
      '',
      `Name:    ${formValues.name}`,
      `Email:   ${formValues.email}`,
      `Phone:   ${formValues.phone || '—'}`,
      `Company: ${formValues.company || '—'}`,
      `PO / Order No: ${formValues.poNumber || '—'}`,
      `Include Courier: ${formValues.courier || 'Not specified'}`,
      '',
      '--- Requested Items ---',
      ...items.map(i =>
        `• ${i.productTitle}` +
        (i.variantTitle ? ` / ${i.variantTitle}` : '') +
        (i.sku ? ` (SKU: ${i.sku})` : '') +
        `  × ${i.quantity}`
      ),
      '',
      '--- Notes ---',
      formValues.notes || 'None',
    ];
    return lines.join('\n');
  }

  // Single source of truth for quote-form validity. Returns true only
  // if the form is safe to submit. Called from the capture-phase submit
  // listener BEFORE the default navigation, so returning false there and
  // calling preventDefault keeps the page on the same URL with the
  // drawer open and the warning visible.
  function isQuoteFormValid(form) {
    if (items.length === 0) {
      alert('Please add at least one product to your order request.');
      return false;
    }

    const nameInput  = form.querySelector('[name="contact[name]"]');
    const emailInput = form.querySelector('[name="contact[email]"]');
    const name  = nameInput.value.trim();
    const email = emailInput.value.trim();

    if (!name) {
      alert('Please enter your name.');
      nameInput.focus();
      return false;
    }

    // Validate the email FORMAT, not just presence. A malformed address
    // (e.g. "asdf" or "jane@") must NOT submit — otherwise the page
    // navigates, the success banner shows, and the order is cleared on
    // an address the store can never reply to.
    emailInput.setCustomValidity('');
    const emailFormatOk =
      emailInput.checkValidity() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!emailFormatOk) {
      emailInput.setCustomValidity('Please enter a valid email address.');
      emailInput.reportValidity(); // shows the message on the field itself
      emailInput.focus();
      return false;
    }

    return true;
  }

  // Fire-and-forget POST of the submitted order to the independent audit
  // log (Google Sheet via Apps Script). Never throws, never blocks: any
  // failure here must NOT stop the real form submission. Uses keepalive so
  // the request survives the page navigation that form.submit() triggers a
  // few milliseconds later.
  function logSubmission(formValues) {
    if (!AUDIT_LOG_URL) return; // logging not configured — skip silently

    const payload = {
      submittedAt: new Date().toISOString(),
      name:     formValues.name,
      email:    formValues.email,
      phone:    formValues.phone,
      company:  formValues.company,
      poNumber: formValues.poNumber,
      courier:  formValues.courier,
      notes:    formValues.notes,
      itemCount: items.reduce((s, i) => s + i.quantity, 0),
      items: items.map(i => ({
        productTitle: i.productTitle,
        variantTitle: i.variantTitle,
        sku:          i.sku,
        quantity:     i.quantity
      })),
      // A plain-text version of the order for easy reading in the sheet
      body: buildBody(formValues),
      pageUrl: window.location.href
    };

    try {
      // text/plain avoids a CORS preflight (Apps Script handles POST body
      // as e.postData.contents regardless of content-type). We don't read
      // the response — this is one-way logging.
      fetch(AUDIT_LOG_URL, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload)
      }).catch(() => {});
    } catch (err) {
      // Never let logging break submission.
      console.warn('[QuoteCart] Audit log POST failed (order still submitted):', err);
    }
  }

  function submitQuote(e) {
    e.preventDefault();

    // Validation already passed in the capture-phase listener, but guard
    // again in case submitQuote is ever called directly.
    const form = document.getElementById('quote-contact-form');
    if (!isQuoteFormValid(form)) return;

    const btn = document.getElementById('quote-submit-btn');

    const formValues = {
      name:    form.querySelector('[name="contact[name]"]').value.trim(),
      email:   form.querySelector('[name="contact[email]"]').value.trim(),
      phone:   form.querySelector('[name="contact[phone]"]')?.value.trim() || '—',
      company: form.querySelector('[name="contact[company]"]')?.value.trim() || '—',
      poNumber: form.querySelector('[name="contact[po_number]"]')?.value.trim() || '—',
      courier: form.querySelector('[name="contact[include_courier]"]:checked')?.value || 'Not specified',
      notes:   form.querySelector('[name="contact[notes]"]')?.value.trim() || 'None',
    };

    // Populate the hidden body field with the full quote
    form.querySelector('[name="contact[body]"]').value = buildBody(formValues);

    btn.disabled    = true;
    btn.textContent = 'Sending…';

    // Independent audit log FIRST (fire-and-forget, keepalive) so the order
    // is recorded even if the email is later dropped by the mail server.
    // This runs before form.submit() but does not wait on it.
    logSubmission(formValues);

    // Save the cart so we can archive it on return. sessionStorage handles
    // the normal same-tab redirect; localStorage is a fallback in case the
    // tab/session is interrupted before checkPostSubmitReturn() runs.
    // We use native form.submit() rather than fetch/XHR because Shopify's
    // bot-detection flags programmatic POSTs and redirects to a /challenge
    // page. A real browser form submit carries all the right cookies/headers
    // and passes the check cleanly. The page will navigate to
    // ?contact_posted=true on submit — checkPostSubmitReturn() handles that.
    sessionStorage.setItem('quote_pending', JSON.stringify(items));
    localStorage.setItem('quote_pending_fallback', JSON.stringify(items));
    form.submit();
  }

  // Runs once on init. If we're returning from a Shopify contact-form POST
  // (?contact_posted=true), archive the submitted cart to a recovery key and
  // reset the LIVE cart — but do NOT permanently destroy anything.
  //
  // IMPORTANT: contact_posted=true only means Shopify ACCEPTED and QUEUED the
  // email. It does NOT confirm the recipient's mail server delivered it. So we
  // never treat this as proof of receipt: we keep a timestamped copy of the
  // order for RECOVERY_DAYS so a "we never got it" report is recoverable from
  // the customer's own browser, and we word the banner as "submitted" rather
  // than as a delivery guarantee.
  function checkPostSubmitReturn() {
    if (!window.location.search.includes('contact_posted=true')) return;

    const pending = sessionStorage.getItem('quote_pending')
                 || localStorage.getItem('quote_pending_fallback');
    if (!pending) return;
    sessionStorage.removeItem('quote_pending');
    localStorage.removeItem('quote_pending_fallback');

    // Archive the submitted order to a recovery log in localStorage before
    // resetting the live cart. Keep recent submissions, pruned by age.
    try {
      const RECOVERY_KEY  = 'b2b_quote_submitted';
      const RECOVERY_DAYS = 14;
      const now = Date.now();
      let archive = [];
      try { archive = JSON.parse(localStorage.getItem(RECOVERY_KEY) || '[]'); }
      catch { archive = []; }

      archive.push({ submittedAt: now, items: JSON.parse(pending) });

      // Prune anything older than RECOVERY_DAYS
      const cutoff = now - RECOVERY_DAYS * 24 * 60 * 60 * 1000;
      archive = archive.filter(entry => (entry.submittedAt || 0) >= cutoff);

      localStorage.setItem(RECOVERY_KEY, JSON.stringify(archive));
    } catch (err) {
      // If archiving fails for any reason, do NOT clear the live cart — the
      // customer keeps their list rather than losing everything.
      console.warn('[QuoteCart] Could not archive submitted order:', err);
      showSubmittedBanner();
      return;
    }

    // Reset the LIVE cart so the badge clears and the UI is ready for a new
    // order — but the recovery copy above survives.
    clear();

    showSubmittedBanner();
  }

  function showSubmittedBanner() {
    const banner = document.createElement('div');
    banner.style.cssText = [
      'position:fixed', 'top:24px', 'left:50%', 'transform:translateX(-50%)',
      'background:#1a1a1a', 'color:#fff', 'padding:16px 28px',
      'border-radius:8px', 'font-size:15px', 'z-index:99999',
      'box-shadow:0 4px 20px rgba(0,0,0,0.3)', 'text-align:center',
      'max-width:90vw', 'line-height:1.5'
    ].join(';');
    // Wording: "submitted", not "sent/delivered". Tells the user what to do
    // if they DON'T hear back, turning a silent failure into a follow-up.
    banner.innerHTML =
      '✓ &nbsp;Order request submitted. We\'ll be in touch within 1–2 business days.'
      + '<br><span style="font-size:13px;opacity:0.85;">'
      + 'If you don\'t hear from us, please email info@worxofafrica.co.za directly.'
      + '</span>';
    document.body.appendChild(banner);
    setTimeout(() => banner.remove(), 9000);
  }

  function showSuccess() {
    const panel = document.getElementById('quote-drawer-panel');
    if (!panel) return;
    panel.innerHTML = `
      <div class="quote-success">
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        <h3>Order Request Sent!</h3>
        <p>Thank you. We'll review your request and get back to you with pricing and availability.</p>
        <button class="quote-close-btn" onclick="QuoteCart.hideDrawer()">Continue Browsing</button>
      </div>`;
  }

  /* ─── Resolve variant metadata from Shopify API ─────────────── */

  // Shopify's /variants/ID.json returns:
  //   { product_variant: { id, product_id, title, sku, image_id, ... } }
  // There is NO product title or image URL in this response.
  //
  // /products/{handle}.js returns full product data (title, variants,
  // images, etc.) — this is the proper endpoint to use, but it requires
  // the product *handle*, not the product *id*.
  //
  // /products.json on the storefront is paginated to 30 by default and
  // does NOT support the ?ids= filter — that param only works on the
  // Admin API. So it can't be used to fetch arbitrary single products
  // on stores with more than 30 products in the catalog.
  //
  // Strategy:
  //   1. Cache product_id → handle whenever we see one in the DOM
  //      (product cards, product pages, bulk-add popups).
  //   2. Prefer fetching by handle (fast, reliable).
  //   3. If we only have a product_id, do a paginated walk of
  //      /products.json?limit=250 until we find it, and cache the handle.
  //
  // We cache the product *data* per product_id so multiple variants from
  // the same product only trigger one product fetch.

  const productCache = new Map();        // product_id → Promise<product|null>
  const idToHandleCache = new Map();     // product_id → handle (string)

  function rememberHandle(productId, handle) {
    if (productId && handle) idToHandleCache.set(String(productId), handle);
  }

  function fetchProductByHandle(handle) {
    return fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
  }

  // Walk paginated /products.json to find a product by id.
  // Stops when found or when a page returns fewer than 250 products.
  function findProductByIdViaCatalog(productId) {
    productId = String(productId);
    let page = 1;
    const walk = () => fetch(`/products.json?limit=250&page=${page}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const products = data?.products || [];
        const match = products.find(p => String(p.id) === productId);
        if (match) {
          rememberHandle(productId, match.handle);
          return match;
        }
        if (products.length < 250) return null; // last page, not found
        page += 1;
        if (page > 20) return null;             // hard safety cap (5000 products)
        return walk();
      })
      .catch(() => null);
    return walk();
  }

  function fetchProduct(productId, hintHandle) {
    productId = String(productId);
    if (productCache.has(productId)) return productCache.get(productId);

    const handle = hintHandle || idToHandleCache.get(productId);
    const promise = (handle
      ? fetchProductByHandle(handle).then(p => {
          if (p) return p;
          // Handle was stale (product renamed) — fall back to catalog walk
          return findProductByIdViaCatalog(productId);
        })
      : findProductByIdViaCatalog(productId)
    ).then(product => {
      if (product?.handle) rememberHandle(productId, product.handle);
      return product || null;
    });

    productCache.set(productId, promise);
    return promise;
  }

  // Scan the page for product_id → handle mappings exposed by Dawn.
  // Dawn renders product cards with anchors like <a href="/products/{handle}">
  // and product forms with hidden product_id inputs. Bulk-add modals expose
  // product_id via data-product-id on form / quick-add elements.
  function harvestHandlesFromDom(root) {
    root = root || document;
    // Look for any element that exposes both a product id and a handle/url
    root.querySelectorAll('[data-product-id]').forEach(el => {
      const pid = el.dataset.productId;
      if (!pid) return;
      // Look for a nearby product link
      const link =
        el.querySelector?.('a[href*="/products/"]') ||
        el.closest('.card-wrapper, .product-card-wrapper, .grid__item, product-form, form')?.querySelector('a[href*="/products/"]');
      const href = link?.getAttribute('href') || '';
      const m = href.match(/\/products\/([^/?#]+)/);
      if (m) rememberHandle(pid, m[1]);
    });
  }

  function resolveVariantMeta(variantId, quantity) {
    fetch(`/variants/${variantId}.json`)
      .then(r => r.json())
      .then(data => {
        const v = data.product_variant || data.variant || {};
        const variantTitle = v.title && v.title !== 'Default Title' ? v.title : '';
        const sku = v.sku || '';
        const productId = v.product_id;

        if (!productId) {
          // No product_id — fall back to minimal add
          add(variantId, quantity, 'Product', variantTitle, sku, '', '');
          return;
        }

        return fetchProduct(productId).then(product => {
          if (!product) {
            add(variantId, quantity, 'Product', variantTitle, sku, '', '');
            return;
          }
          const productTitle = product.title || 'Product';
          const handle = product.handle || '';
          // Find the matching variant to get its featured image
          const matchedVariant = (product.variants || [])
            .find(pv => String(pv.id) === String(variantId));
          let imageUrl = '';
          // Try variant-specific image first, then product's first image
          const rawImage = matchedVariant?.featured_image?.src
            || product.images?.[0]?.src
            || product.featured_image
            || '';
          if (rawImage) {
            imageUrl = rawImage.replace(/(\.[^.?]+)(\?.*)?$/, '_80x80$1$2');
          }
          add(variantId, quantity, productTitle, variantTitle, sku, handle, imageUrl);
        });
      })
      .catch(() => add(variantId, quantity, 'Product', '', '', '', ''));
  }

  /* ─── Intercept fetch (cart/add, cart/change, cart/update) ───── */

  // Extract variant id + quantity from any of Shopify's body shapes:
  //   • Form-encoded: { id, quantity }
  //   • Items-array (JSON):    { items: [{ id, quantity }, ...] }
  //   • Bracket-flattened:     { 'items[0][id]', 'items[0][quantity]' }
  function extractAddPayload(data) {
    const out = [];
    if (!data) return out;

    // Shape 1: top-level id
    if (data.id) {
      out.push({ id: String(data.id), quantity: parseInt(data.quantity || '1', 10) });
      return out;
    }
    // Shape 2: real items array
    if (Array.isArray(data.items)) {
      data.items.forEach(it => {
        if (it?.id) out.push({
          id: String(it.id),
          quantity: parseInt(it.quantity || '1', 10),
        });
      });
      if (out.length) return out;
    }
    // Shape 3: bracket-flattened items[N][id]
    const bracketIds = {};
    Object.keys(data).forEach(k => {
      const m = k.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (m) {
        bracketIds[m[1]] = bracketIds[m[1]] || {};
        bracketIds[m[1]][m[2]] = data[k];
      }
    });
    Object.values(bracketIds).forEach(it => {
      if (it.id) out.push({
        id: String(it.id),
        quantity: parseInt(it.quantity || '1', 10),
      });
    });
    return out;
  }

  function interceptFetch() {
    const origFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';

      if (/\/cart\/add/.test(url)) {
        const data = parseBody(init?.body);
        const adds = extractAddPayload(data);
        if (adds.length) {
          adds.forEach(({ id, quantity }) => resolveVariantMeta(id, quantity));
          return fakeCartResponse();
        }
      }

      if (/\/cart\/change/.test(url)) {
        const data = parseBody(init?.body);
        const variantId = String(data.id || data.variant_id || '');
        const quantity = parseInt(data.quantity, 10);
        if (variantId && !isNaN(quantity)) {
          if (quantity <= 0) {
            remove(variantId);
          } else {
            const existing = items.find(i => i.variantId === variantId);
            if (existing) { existing.quantity = quantity; save(); render(); }
            else { resolveVariantMeta(variantId, quantity); }
          }
          return fakeCartResponse();
        }
      }

      if (/\/cart\/update/.test(url)) {
        const data = parseBody(init?.body);
        if (data.updates) {
          Object.entries(data.updates).forEach(([vid, qty]) => {
            qty = parseInt(qty, 10);
            if (qty <= 0) { remove(String(vid)); }
            else {
              const ex = items.find(i => i.variantId === String(vid));
              if (ex) { ex.quantity = qty; save(); }
              else { resolveVariantMeta(String(vid), qty); }
            }
          });
          render();
          return fakeCartResponse();
        }
      }

      return origFetch.apply(this, arguments);
    };
  }

  /* ─── Intercept XMLHttpRequest (some Shopify code paths use XHR) ───── */

  function interceptXHR() {
    const OrigXHR = window.XMLHttpRequest;
    if (!OrigXHR) return;
    const origOpen = OrigXHR.prototype.open;
    const origSend = OrigXHR.prototype.send;

    OrigXHR.prototype.open = function (method, url) {
      this.__qcUrl = url;
      this.__qcMethod = method;
      return origOpen.apply(this, arguments);
    };

    OrigXHR.prototype.send = function (body) {
      const url = this.__qcUrl || '';
      if (/\/cart\/add/.test(url)) {
        const data = parseBody(body);
        const adds = extractAddPayload(data);
        if (adds.length) {
          adds.forEach(({ id, quantity }) => resolveVariantMeta(id, quantity));
          // Fake a successful response so the page's JS doesn't error out
          Object.defineProperty(this, 'readyState',   { configurable: true, get: () => 4 });
          Object.defineProperty(this, 'status',       { configurable: true, get: () => 200 });
          Object.defineProperty(this, 'responseText', { configurable: true, get: () => JSON.stringify({ items: [] }) });
          Object.defineProperty(this, 'response',     { configurable: true, get: () => JSON.stringify({ items: [] }) });
          setTimeout(() => {
            this.onreadystatechange?.();
            this.onload?.();
          }, 0);
          return;
        }
      }
      return origSend.apply(this, arguments);
    };
  }

  function parseBody(body) {
    if (!body) return {};
    if (body instanceof FormData) return Object.fromEntries(body.entries());
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch { }
      try { return Object.fromEntries(new URLSearchParams(body)); } catch { }
    }
    return {};
  }

  function fakeCartResponse() {
    const fakeCart = {
      token: 'b2b-quote-mode',
      note: null,
      attributes: {},
      original_total_price: 0,
      total_price: 0,
      total_discount: 0,
      total_weight: 0.0,
      item_count: 0,
      items: [],
      requires_shipping: false,
      currency: window.Shopify?.currency?.active || 'USD',
      items_subtotal_price: 0,
      cart_level_discount_applications: [],
      checkout_charge_amount: 0
    };
    return Promise.resolve(new Response(
      JSON.stringify(fakeCart),
      { status: 200, headers: { 'Content-Type': 'application/json' } }
    ));
  }

  /* ─── Intercept form-based add-to-cart ──────────────────────── */

  function interceptForms() {
    document.addEventListener('submit', e => {
      const form = e.target;
      if (!form.action?.includes('/cart/add') &&
          !form.getAttribute('action')?.includes('/cart/add')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const variantId = form.querySelector('[name="id"]')?.value;
      const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
      // If we're on a product page, capture the handle from the URL
      const pageHandleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
      const productIdInput = form.querySelector('[name="product-id"], [name="product_id"]')?.value;
      if (pageHandleMatch && productIdInput) {
        rememberHandle(productIdInput, pageHandleMatch[1]);
      }
      if (variantId) resolveVariantMeta(variantId, quantity);
    }, true);
  }

  /* ─── Catch-all click handler on quick-add buttons ─────────────
     For no-variant products, Dawn's product-form-component on cards
     may send the cart/add request in a way our other interceptors
     can miss (some Dawn versions send {items: [...]} JSON, others
     submit synchronously, others use XHR). This click listener fires
     synchronously the moment the user taps the button and reads the
     variant ID directly from the surrounding <product-form>, so we
     always capture the add even if the network request itself slips
     past. We then resolve metadata and the request is harmlessly
     intercepted (or not) by the fetch/XHR layer.
  */

  function interceptQuickAddClicks() {
    // Capture phase so we run before any other handlers prevent default
    document.addEventListener('click', e => {
      // Quick-add buttons live inside <product-form> on cards.
      // Also handles older `.quick-add__submit` markup.
      const btn = e.target.closest(
        'product-form button[name="add"], ' +
        'product-form button[type="submit"], ' +
        '.quick-add__submit, ' +
        '.card__product-form__submit'
      );
      if (!btn) return;

      // Find the enclosing <product-form> or <form>
      const productForm = btn.closest('product-form');
      const form = btn.closest('form') || productForm?.querySelector('form');
      if (!form) return;

      // Don't double-fire if the form's action is /cart/add — interceptForms
      // will handle it. Only run when we're outside an obvious /cart/add form
      // OR when the form has no action (which is what often happens on cards).
      // Just call resolveVariantMeta either way; resolveVariantMeta dedupes
      // via items.find() in add(), so a duplicate call is harmless.

      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput?.value) return;

      const variantId = variantInput.value;
      const qtyInput = form.querySelector('[name="quantity"]');
      const quantity = parseInt(qtyInput?.value || '1', 10) || 1;

      // Capture handle from any product link in the same card
      const card = btn.closest('.card-wrapper, .product-card-wrapper, li.grid__item');
      const productLinkInCard = card?.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
      const handleMatch = productLinkInCard.match(/\/products\/([^/?#]+)/);
      const productHandle = handleMatch ? handleMatch[1] : '';

      // Stop Dawn's default behaviour so the real cart never gets touched
      e.preventDefault();
      e.stopImmediatePropagation();

      // Resolve metadata + add (resolveVariantMeta handles duplicates)
      resolveVariantMetaWithHandle(variantId, quantity, productHandle);
    }, true);
  }

  // Variant of resolveVariantMeta that accepts a hint handle and feeds it
  // into the productCache lookup path.
  function resolveVariantMetaWithHandle(variantId, quantity, handleHint) {
    // De-dupe: if already in quote, just increment.
    const existing = items.find(i => i.variantId === String(variantId));
    if (existing) {
      existing.quantity += quantity;
      save();
      render();
      flashBadge();
      return;
    }

    fetch(`/variants/${variantId}.json`)
      .then(r => r.json())
      .then(data => {
        const v = data.product_variant || data.variant || {};
        const variantTitle = v.title && v.title !== 'Default Title' ? v.title : '';
        const sku = v.sku || '';
        const productId = v.product_id;

        if (!productId) {
          add(variantId, quantity, 'Product', variantTitle, sku, handleHint || '', '');
          return;
        }
        if (handleHint) rememberHandle(productId, handleHint);

        return fetchProduct(productId, handleHint).then(product => {
          if (!product) {
            add(variantId, quantity, 'Product', variantTitle, sku, handleHint || '', '');
            return;
          }
          const productTitle = product.title || 'Product';
          const handle = product.handle || handleHint || '';
          const matchedVariant = (product.variants || [])
            .find(pv => String(pv.id) === String(variantId));
          let imageUrl = '';
          const rawImage = matchedVariant?.featured_image?.src
            || product.images?.[0]?.src
            || product.featured_image
            || '';
          if (rawImage) {
            imageUrl = rawImage.replace(/(\.[^.?]+)(\?.*)?$/, '_80x80$1$2');
          }
          add(variantId, quantity, productTitle, variantTitle, sku, handle, imageUrl);
        });
      })
      .catch(() => add(variantId, quantity, 'Product', '', '', handleHint || '', ''));
  }

  /* ─── Direct listener for Quick Add quantity inputs ───────────
     Fires whenever a .quantity__input[data-quantity-variant-id] changes,
     in any context:
       • Inside the bulk-add popup (products with variants)
       • Directly on a catalogue card (no-variant products — Dawn renders
         a <quick-add-bulk> stepper right on the card instead of a popup)

     We can't filter on `isTrusted` because Dawn's own +/- buttons
     dispatch programmatic (untrusted) change events when clicked.
     Instead we tag our internal events with `__qcInternal = true` in
     resetPopupDefaults() and ignore those specifically.
  */

  function interceptBulkQuickAdd() {
    document.addEventListener('change', e => {
      // Ignore the programmatic change events we dispatch ourselves in
      // syncInputsFromCart — those are tagged with __qcInternal. (User
      // edits from Dawn's +/- buttons are untrusted too, so we can't use
      // isTrusted to tell them apart — hence the explicit tag.)
      if (e.__qcInternal) return;

      const input = e.target;
      if (!input.matches('.quantity__input[data-quantity-variant-id]')) return;

      const variantId = String(input.dataset.quantityVariantId);
      const quantity = parseInt(input.value, 10);
      if (!variantId) return;

      if (isNaN(quantity) || quantity <= 0) { remove(variantId); return; }

      // Already in quote: the input is now SEEDED from the quote cart by
      // syncInputsFromCart() before any user interaction, so its value
      // reflects the true quoted quantity. A user "+" click therefore
      // produces (quoted + 1), and absolute assignment is correct.
      // (This is the fix for the old desync: previously the input value
      // came from Shopify's always-empty native cart, so assigning it
      // here clobbered the real quote quantity.)
      const existing = items.find(i => i.variantId === variantId);
      if (existing) {
        existing.quantity = quantity;
        save();
        render();
        flashBadge();
        return;
      }

      // ─── Gather metadata from the DOM ────────────────────────────
      // The input lives in one of two contexts:
      //   A) Inside a bulk-add modal — variant row + dialog aria-label
      //   B) On a catalogue card  — <quick-add-bulk> on .card-wrapper
      //
      // We try both. Dawn helpfully puts data-title / data-sku / data-img
      // directly on the .quantity__input on cards, which we use too.

      // Try bulk-add modal markup first
      const row = document.getElementById('Variant-' + variantId);
      let variantTitle = row?.querySelector('.variant-item__name')?.textContent?.trim() || '';

      const dialog = input.closest('[role="dialog"]');
      const dialogLabel = dialog?.getAttribute('aria-label') || '';
      let productTitle = dialogLabel.replace(/^choose options for\s+/i, '').trim();

      let productHandle = '';
      const modalProductLink = dialog?.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
      const modalHandleMatch = modalProductLink.match(/\/products\/([^/?#]+)/);
      if (modalHandleMatch) productHandle = modalHandleMatch[1];

      // If we couldn't get title from the modal, this is almost certainly
      // a catalogue card. Look for the card-wrapper around the input.
      const card = input.closest('.card-wrapper, .product-card-wrapper, li.grid__item');
      if (!productTitle && card) {
        // Card title sits in .card__heading > a
        const titleAnchor = card.querySelector('.card__heading a, .card__information a');
        productTitle = titleAnchor?.textContent?.trim() || '';
      }
      if (!productHandle && card) {
        const cardLink = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
        const cardHandleMatch = cardLink.match(/\/products\/([^/?#]+)/);
        if (cardHandleMatch) productHandle = cardHandleMatch[1];
      }

      // For no-variant products on cards, "variant title" is meaningless
      // (Default Title) — leave it blank rather than showing "Default Title".
      const dataTitle = input.dataset.title || '';
      if (!variantTitle && dataTitle && dataTitle !== 'Default Title') {
        variantTitle = dataTitle;
      }

      // SKU and image are also exposed on data-attributes on cards
      const sku = input.dataset.sku || '';
      let imageUrl = input.dataset.img && !input.dataset.img.startsWith('Liquid error')
        ? input.dataset.img
        : '';

      // If we still don't have an image, grab it from the card
      if (!imageUrl && card) {
        const cardImg = card.querySelector('.card__media img, .media img');
        const rawImage = cardImg?.currentSrc || cardImg?.src || '';
        if (rawImage) {
          // strip any width param and add a fixed _80x80 size
          imageUrl = rawImage
            .replace(/[?&]width=\d+/, '')
            .replace(/(\.[^.?]+)(\?.*)?$/, '_80x80$1$2');
        }
      }

      if (productTitle) {
        // We have at minimum a title — add immediately, enrich in background
        add(variantId, quantity, productTitle, variantTitle, sku, productHandle, imageUrl);

        // Background enrichment: only run if we're missing SKU or image
        if (!sku || !imageUrl) {
          fetch(`/variants/${variantId}.json`)
            .then(r => r.json())
            .then(data => {
              const v = data.product_variant || data.variant || {};
              const item = items.find(i => i.variantId === variantId);
              if (!item) return;
              if (!item.sku && v.sku) item.sku = v.sku;
              const productId = v.product_id;
              if (!productId) { save(); render(); return; }
              if (productHandle) rememberHandle(productId, productHandle);
              return fetchProduct(productId, productHandle).then(product => {
                if (!product) { save(); render(); return; }
                if (!item.imageUrl) {
                  const matchedVariant = (product.variants || [])
                    .find(pv => String(pv.id) === String(variantId));
                  const rawImage = matchedVariant?.featured_image?.src
                    || product.images?.[0]?.src
                    || product.featured_image
                    || '';
                  if (rawImage) {
                    item.imageUrl = rawImage.replace(/(\.[^.?]+)(\?.*)?$/, '_80x80$1$2');
                  }
                }
                save();
                render();
              });
            })
            .catch(() => {});
        }
      } else {
        // No title from DOM — full API fallback
        resolveVariantMeta(variantId, quantity);
      }
    });
  }

  /* ─── Inject "View Order Form" button into quick-order-list ──── */

  function injectViewOrderButtons() {
    // Watch for details elements being toggled open
    document.addEventListener('click', e => {
      // Small delay to let Dawn render the quick-order-list content
      setTimeout(() => {
        document.querySelectorAll('quick-order-list, .quick-order-list').forEach(injectOrderBtn);
      }, 300);
    });
    // Also handle any already-open ones on init
    document.querySelectorAll('quick-order-list, .quick-order-list').forEach(injectOrderBtn);
  }

  function injectOrderBtn(list) {
    if (list.querySelector('.qc-popup-footer')) return;

    const div = document.createElement('div');
    div.className = 'qc-popup-footer';

    // "Continue Shopping" — closes the popup by closing its parent <details>
    const continueBtn = document.createElement('button');
    continueBtn.className = 'qc-continue-btn';
    continueBtn.type = 'button';
    continueBtn.textContent = 'Continue Shopping';
    continueBtn.addEventListener('click', () => {
      // Exact structure from rendered HTML:
      // <modal-dialog class="quick-add-modal">
      //   <button class="quick-add-modal__toggle">X</button>  ← this is the close button
      //   <div>...<quick-order-list>...</quick-order-list></div>
      // </modal-dialog>
      list.closest('modal-dialog')
          ?.querySelector('.quick-add-modal__toggle')
          ?.click();
    });

    // "View Order Form" — opens the quote drawer
    const orderBtn = document.createElement('button');
    orderBtn.className = 'qc-view-order-btn';
    orderBtn.type = 'button';
    orderBtn.textContent = 'View Order Form';
    orderBtn.addEventListener('click', showDrawer);

    div.appendChild(continueBtn);
    div.appendChild(orderBtn);

    // Prefer the __total container (now visible after CSS fix).
    // Fall back to the inner form, then the list element itself.
    const target =
      list.querySelector('.quick-order-list__total') ||
      list.querySelector('form') ||
      list;
    target.appendChild(div);
  }

  /* ─── Fix quick-order-list popup layout ───────────────────────── */
  /*
     Why inline-!important via style.setProperty:
       CSS !important can be beaten by Dawn's own !important rules.
       style.setProperty(prop, val, 'important') writes an INLINE
       important style — the absolute top of the cascade. Nothing wins.

     Why MutationObserver + requestAnimationFrame:
       Dawn's <quick-order-list> custom element re-renders its <tbody>
       rows asynchronously after the popup opens, wiping out any styles
       we applied to the original nodes. We watch specifically for new
       .variant-item nodes being inserted, then immediately queue a fix
       via requestAnimationFrame so we run right after Dawn's render,
       before the browser paints.
  */

  function fixRow(row) {
    row.style.setProperty('display', 'flex', 'important');
    row.style.setProperty('align-items', 'center', 'important');
    row.style.setProperty('padding', '8px 0', 'important');
    row.style.setProperty('border-bottom', '1px solid #e0e0e0', 'important');
    row.style.setProperty('width', '100%', 'important');

    // First cell (thumbnail + variant name): fill available space
    const firstCell = row.querySelector('td:first-child, th:first-child');
    if (firstCell) {
      firstCell.style.setProperty('flex', '1 1 auto', 'important');
      firstCell.style.setProperty('min-width', '0', 'important');
      firstCell.style.setProperty('padding', '0', 'important');
      firstCell.style.setProperty('overflow', 'hidden', 'important');
    }

    // Quantity cell: shrink to stepper width only
    const qtyCell = row.querySelector('td.variant-item__quantity, .variant-item__quantity');
    if (qtyCell) {
      qtyCell.style.setProperty('flex', '0 0 auto', 'important');
      qtyCell.style.setProperty('width', 'auto', 'important');
      qtyCell.style.setProperty('padding', '0 0 0 12px', 'important');
      qtyCell.style.setProperty('display', 'flex', 'important');
      qtyCell.style.setProperty('align-items', 'center', 'important');
    }

    // Price/totals cells: remove from layout entirely
    row.querySelectorAll('td.variant-item__price, td.variant-item__totals')
       .forEach(td => td.style.setProperty('display', 'none', 'important'));
  }

  function fixPopupLayout() {
    // ① Strip table display so flex can take over the rows
    document.querySelectorAll('.quick-order-list__table').forEach(table => {
      table.style.setProperty('display', 'block', 'important');
      table.style.setProperty('width', '100%', 'important');
      const thead = table.querySelector('thead');
      if (thead) thead.style.setProperty('display', 'none', 'important');
      const tbody = table.querySelector('tbody');
      if (tbody) {
        tbody.style.setProperty('display', 'block', 'important');
        tbody.style.setProperty('width', '100%', 'important');
      }
    });

    // ② Fix every variant row
    document.querySelectorAll('.variant-item').forEach(fixRow);

    // ③ Hide Dawn's remove/trash buttons via JS — quantity-popover.js sets
    //    inline style="display:block" on init, which beats CSS. Only
    //    inline !important wins.
    document.querySelectorAll('.quick-order-list-remove-button').forEach(btn => {
      btn.style.setProperty('display', 'none', 'important');
    });

    // ④ Nuke all native __total content; only our .qc-popup-footer survives
    document.querySelectorAll('.quick-order-list__total').forEach(total => {
      total.style.setProperty('display', 'block', 'important');
      total.style.setProperty('padding', '0', 'important');
      Array.from(total.children).forEach(child => {
        if (!child.classList.contains('qc-popup-footer')) {
          child.style.setProperty('display', 'none', 'important');
        }
      });
    });

    // ⑤ Hydrate every quantity input from the quote cart (source of
    //    truth). Replaces the old one-way resetPopupDefaults(), which
    //    only forced unquoted inputs to 0 but never restored the real
    //    quantity for variants already in the quote — that gap is why a
    //    popup reopened after navigation showed 0 for an already-quoted
    //    item.
    syncInputsFromCart();

    // ⑥ Re-inject footer buttons in case __total was just re-rendered
    document.querySelectorAll('quick-order-list, .quick-order-list')
      .forEach(injectOrderBtn);
  }

  function watchAndFixPopupLayout() {
    fixPopupLayout(); // Run once on init in case any popup is already in DOM

    let rafPending = false;
    const observer = new MutationObserver(mutations => {
      if (rafPending) return;
      let relevant = false;
      outer: for (const mut of mutations) {
        // Dawn opens popups by adding the 'open' attribute to <modal-dialog>
        // — no new nodes are inserted, so childList alone never fires.
        if (mut.type === 'attributes') {
          relevant = true;
          break outer;
        }
        // Also catch cases where Dawn re-renders rows or the total area
        for (const node of mut.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.classList?.contains('variant-item') ||
            node.classList?.contains('quick-order-list__table') ||
            node.querySelector?.('.variant-item, .quick-order-list__table, .quick-order-list__total')
          ) {
            relevant = true;
            break outer;
          }
        }
      }
      if (!relevant) return;
      rafPending = true;
      requestAnimationFrame(() => {
        fixPopupLayout();
        harvestHandlesFromDom();
        rafPending = false;
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,         // watch attribute changes too
      attributeFilter: ['open'] // only fire for the 'open' attr on modal-dialog
    });

    [250, 500, 1000, 2000].forEach(ms => setTimeout(fixPopupLayout, ms));
  }

  // DEPRECATED — superseded by syncInputsFromCart(), which does a full
  // two-way hydrate (sets quoted variants to their real quantity AND
  // unquoted ones to 0) instead of this one-way "force unquoted to 0".
  // Kept only for reference / safety; no longer called anywhere.
  function resetPopupDefaults() {
    const quotedIds = new Set(items.map(i => String(i.variantId)));
    document.querySelectorAll('.quantity__input[data-quantity-variant-id]').forEach(input => {
      const vid = String(input.dataset.quantityVariantId);
      if (!quotedIds.has(vid) && parseInt(input.value || '0', 10) > 0) {
        input.value = '0';
        const evt = new Event('change', { bubbles: true });
        // Tag this event so interceptBulkQuickAdd can ignore it.
        // We can't rely on isTrusted because Dawn's +/- buttons also
        // dispatch untrusted change events when users click them.
        evt.__qcInternal = true;
        input.dispatchEvent(evt);
      }
    });
  }

  /* ─── Inject "View details" link onto each product card ──────── */
  /*
     The bulk quick-add button doesn't make it obvious that users can
     also click through to the full product page. We add a small
     "View details" link inside each card that links to the product URL
     (found on the card title anchor).

     Scoped to .card-wrapper elements that contain .quick-add-bulk —
     this ensures we only touch product cards on collection/featured
     grids, never popup content or other card-like elements.
  */

  function injectProductDetailsLinks() {
    document.querySelectorAll('.card-wrapper').forEach(addDetailsLinkToCard);

    // Watch for lazy-loaded / paginated cards
    const observer = new MutationObserver(mutations => {
      for (const mut of mutations) {
        for (const node of mut.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.classList?.contains('card-wrapper')) {
            addDetailsLinkToCard(node);
          } else {
            node.querySelectorAll?.('.card-wrapper').forEach(addDetailsLinkToCard);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function addDetailsLinkToCard(cardWrapper) {
    // Only touch product cards that have a quick-add button — this
    // excludes collection cards and any other card-like markup.
    if (!cardWrapper.querySelector('.quick-add, quick-add-bulk, .quick-add__submit')) return;
    if (cardWrapper.querySelector('.qc-product-details-link')) return; // already injected

    // Get product URL from the card's title link
    const titleLink = cardWrapper.querySelector('.card__heading a[href]');
    const productUrl = titleLink?.getAttribute('href');
    if (!productUrl) return;

    // Find the outer .card__content (sibling of .card__inner) where the
    // title + quick-add live. That's the only .card__content inside the
    // card that contains the quick-add button.
    const quickAddWrapper = cardWrapper.querySelector('.quick-add, quick-add-bulk');
    const contentArea = quickAddWrapper?.closest('.card__content');
    if (!contentArea) return;

    const link = document.createElement('a');
    link.className = 'qc-product-details-link';
    link.href = productUrl;
    link.textContent = 'View product details';

    // Insert just before the quick-add button block
    contentArea.insertBefore(link, quickAddWrapper);
  }

  /* ─── Hover Zoom Functionality ──────────────────────────────── */

function initHoverZoom() {
  
  // 1. Target the main Product Page images (Normal behavior)
  document.querySelectorAll('.product__media').forEach(container => {
    const images = container.querySelectorAll('img');
    if (images.length === 0) return;
    
    container.style.setProperty('overflow', 'hidden', 'important');

    container.addEventListener('mousemove', (e) => {
      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      
      images.forEach(img => {
        img.style.setProperty('transition', 'transform 0.1s linear', 'important');
        img.style.setProperty('transform-origin', `${x}% ${y}%`, 'important');
        img.style.setProperty('transform', 'scale(2.0)', 'important');
      });
    });

    container.addEventListener('mouseleave', () => {
      images.forEach(img => {
        img.style.setProperty('transition', 'transform 0.3s ease-out', 'important');
        img.style.setProperty('transform', 'scale(1.0)', 'important');
      });
    });
  });

  // 2. Target Dawn Product Cards (The "Invisible Link" Fix)
  document.querySelectorAll('.product-card-wrapper').forEach(card => {
    const inner = card.querySelector('.card__inner'); // The image box
    const images = card.querySelectorAll('img');
    if (!inner || images.length === 0) return;

    inner.style.setProperty('overflow', 'hidden', 'important');

    // We attach the mouse tracker to the whole card so Dawn's link can't block it
    card.addEventListener('mousemove', (e) => {
      const rect = inner.getBoundingClientRect();

      // Math check: Is the cursor actually over the image area? 
      // If it's over the text at the bottom, reset the zoom.
      if (e.clientY < rect.top || e.clientY > rect.bottom || e.clientX < rect.left || e.clientX > rect.right) {
        images.forEach(img => {
          img.style.setProperty('transition', 'transform 0.3s ease-out', 'important');
          img.style.setProperty('transform', 'scale(1.0)', 'important');
        });
        return;
      }

      // If over the image, calculate the pan coordinates
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      images.forEach(img => {
        img.style.setProperty('transition', 'transform 0.1s linear', 'important');
        img.style.setProperty('transform-origin', `${x}% ${y}%`, 'important');
        img.style.setProperty('transform', 'scale(2.0)', 'important');
      });
    });

    // Reset when the mouse leaves the card entirely
    card.addEventListener('mouseleave', () => {
      images.forEach(img => {
        img.style.setProperty('transition', 'transform 0.3s ease-out', 'important');
        img.style.setProperty('transform-origin', 'center center', 'important');
        img.style.setProperty('transform', 'scale(1.0)', 'important');
      });
    });
  });
}

  /* ─── Init ──────────────────────────────────────────────────── */

  function init() {
    load();
    healStoredItems(); // Fix any items previously saved with bad data
    harvestHandlesFromDom();
    render();
    interceptFetch();
    interceptXHR();
    interceptForms();
    interceptQuickAddClicks();
    interceptBulkQuickAdd();
    injectViewOrderButtons();
    watchAndFixPopupLayout();
    injectProductDetailsLinks();
    initHoverZoom();

    // Suppress Dawn's cart error notifications — we don't use the cart
    document.addEventListener('cart-error', e => e.stopImmediatePropagation(), true);
    window.addEventListener('cart:error', e => e.stopImmediatePropagation(), true);

    // Check if we're returning from a successful form submission
    checkPostSubmitReturn();

    // Wire up the quote form submit.
    // We use a CAPTURE-phase listener so this runs before any other
    // submit handler (and before the browser's default navigation).
    // If validation fails we preventDefault + stopImmediatePropagation,
    // which guarantees the page cannot navigate and the drawer stays
    // open with the warning visible — regardless of any other submit
    // path on the form.
    document.addEventListener(
      'submit',
      e => {
        if (e.target.id !== 'quote-contact-form') return;
        if (!isQuoteFormValid(e.target)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return; // blocked: no navigation, drawer stays open
        }
        submitQuote(e);
      },
      true // capture phase
    );
  }

  /* ─── Helpers ───────────────────────────────────────────────── */

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ─── bfcache rehydration ──────────────────────────────────────
     When the user returns via the browser Back/Forward button, the
     browser may restore this page from the back/forward cache: a
     frozen in-memory snapshot whose `items` array predates anything
     they added on the page they just left. DOMContentLoaded does NOT
     fire on a bfcache restore, so init()/load() never re-runs and our
     in-memory `items` stays stale. The next save() would then clobber
     localStorage with that stale array and silently drop the items.

     event.persisted is true exactly in the bfcache-restore case. We
     reload from localStorage (the single source of truth) and repaint.
     render() also runs syncInputsFromCart(), so the popup/PDP steppers
     re-seed too. We reload before any user interaction can fire a
     save(), which closes the data-loss window. */
  function rehydrateFromCache(event) {
    if (!event.persisted) return;
    load();
    harvestHandlesFromDom();
    render();
  }

  return { init, add, remove, setQty, clear, showDrawer, hideDrawer, toggleFullscreen, totalCount, rehydrateFromCache };
})();

document.addEventListener('DOMContentLoaded', QuoteCart.init);
window.addEventListener('pageshow', QuoteCart.rehydrateFromCache);